import { clsx } from "clsx";
import { useState } from "react";

import {
  MatchingContact,
  SBContact,
  SBContactType,
  createAndReplaceCurrentContactPlaceholder,
  createNewContactPlaceholder,
  getContactType,
  lookupMatchingContacts,
  updateCurrentContactPlaceholder,
} from "@smart/bridge-smokeball-basic";
import { useAsync } from "@smart/itops-hooks-dom";
import { loadDefaultCountry } from "@smart/itops-locale-dom";
import { ProgressCircle, Tooltip } from "@smart/itops-sb-design-system-dom";
import { sortField } from "@smart/itops-utils-basic";
import { useLazyLoadContacts } from "@smart/manage-gql-client-dom";

import { ContactItem } from "./contact-item";
import { formatAddressLine, formatPhoneNumber } from "./utils";

type ContactSelectProps = {
  roleName: string;
  hasRoleContact: boolean;
  needReviewBeforeUpdate: boolean | undefined;
  targetContact: Partial<Record<SBContactType, Record<string, any>>>;
  currentContactId: string | undefined;
  selected: string | undefined;
  onSelect: (selected: string) => void;
  onAutoResolve?: () => void;
  getContact:
    | ((contactId: string) => Promise<SBContact | undefined>)
    | undefined;
  openContact: ((id: string) => void) | undefined;
};

export const ContactSelect = ({
  roleName,
  hasRoleContact,
  needReviewBeforeUpdate,
  targetContact,
  currentContactId,
  selected,
  onSelect,
  onAutoResolve,
  getContact,
  openContact,
}: ContactSelectProps) => {
  const [currentContact, setCurrentContact] = useState<SBContact | undefined>(
    undefined,
  );
  const [matches, setMatches] = useState<MatchingContact[]>([]);
  const [loadingMatches, setLoadingMatches] = useState(false);
  const [isGroupOfPeople, setIsGroupOfPeople] = useState(false);
  const [lookupFailed, setLookupFailed] = useState(false);
  const searchContacts = useLazyLoadContacts();

  useAsync(
    async (info) => {
      if (!targetContact || (hasRoleContact && !needReviewBeforeUpdate)) return;

      setLoadingMatches(true);
      setLookupFailed(false);
      try {
        const [matchingContactsPromise, currentContactPromise] =
          await Promise.allSettled([
            lookupMatchingContacts({
              input: targetContact,
              search: async ({ names, emails, phones }) => {
                const searchResult = await searchContacts({
                  names,
                  emails,
                  phones,
                });
                return (searchResult as SBContact[]) || [];
              },
            }),
            getContact && currentContactId
              ? getContact(currentContactId)
              : Promise.resolve(undefined),
          ]);

        /** A newer run of this effect superseded this one.
         * Its own load() call cancelled this run's in-flight
         * request, so any rejection here is stale, not a
         * real failure. Let the newer run's result apply instead.
         */
        if (!info.mounted) return;

        // rejected lookup means we could not verify duplicates
        if (matchingContactsPromise.status === "rejected") {
          console.error(matchingContactsPromise.reason);
          setLookupFailed(true);
        }

        const matchingContacts =
          matchingContactsPromise.status === "fulfilled"
            ? matchingContactsPromise.value
            : [];

        const loadedCurrentContact =
          currentContactPromise.status === "fulfilled"
            ? currentContactPromise.value
            : undefined;
        if (loadedCurrentContact) {
          const firstPersonId = loadedCurrentContact.groupOfPeople?.people?.[0];
          const firstPerson =
            getContact && firstPersonId
              ? await getContact(firstPersonId)
              : undefined;

          setIsGroupOfPeople(
            !!loadedCurrentContact.groupOfPeople?.people?.length,
          );
          setCurrentContact(firstPerson || loadedCurrentContact);
        }

        const filteredMatches = sortField(
          matchingContacts.filter((contact) =>
            currentContactId ? contact.id !== currentContactId : true,
          ),
          { key: "rank", dir: "desc" },
        );
        setMatches(filteredMatches);
        // mark reviewed only when lookup succeeded
        // empty from a rejected lookup is "couldn't check"
        if (
          matchingContactsPromise.status === "fulfilled" &&
          !filteredMatches.length
        ) {
          onSelect(createNewContactPlaceholder);
          if (!needReviewBeforeUpdate) {
            onAutoResolve?.();
          }
        }
        setLoadingMatches(false);
      } catch (e) {
        if (!info.mounted) return;
        console.error(e);
        setLookupFailed(true);
        setLoadingMatches(false);
      }
    },
    [targetContact, hasRoleContact, needReviewBeforeUpdate],
  );

  if (
    !matches.length &&
    !loadingMatches &&
    !needReviewBeforeUpdate &&
    !lookupFailed
  ) {
    return null;
  }

  const indicatorClassNames =
    "relative before:content-[''] before:absolute before:w-full before:bottom-[-1.2rem] before:border-y before:border-neutral-500";

  const headerClassNames =
    "flex items-center gap-4 px-[1.6rem] py-[1.2rem] border-b border-gray-300";

  const newContactPlaceholder =
    hasRoleContact && needReviewBeforeUpdate
      ? createAndReplaceCurrentContactPlaceholder
      : createNewContactPlaceholder;
  const contactTypeMatches =
    currentContact &&
    getContactType(currentContact) === getContactType(targetContact);

  const reviewingCurrentContact =
    needReviewBeforeUpdate &&
    hasRoleContact &&
    currentContact &&
    contactTypeMatches;

  const currentContactName =
    currentContact?.company?.name ||
    currentContact?.trust?.name ||
    [currentContact?.person?.firstName, currentContact?.person?.lastName]
      .filter(Boolean)
      .join(" ");

  const buildChangedItems = () => {
    if (!currentContact) return "";

    const defaultCountry = loadDefaultCountry();
    const items: string[] = [];
    if (
      ((targetContact.person?.firstName !== undefined ||
        targetContact.person?.lastName !== undefined) &&
        currentContact.person?.firstName !== targetContact.person?.firstName) ||
      currentContact.person?.lastName !== targetContact.person?.lastName ||
      (targetContact.company?.name !== undefined &&
        currentContact.company?.name !== targetContact.company?.name) ||
      (targetContact.trust?.name !== undefined &&
        currentContact.trust?.name !== targetContact.trust?.name)
    ) {
      items.push("name");
    }

    if (
      targetContact.person?.cell !== undefined &&
      formatPhoneNumber(currentContact.person?.cell) !==
        formatPhoneNumber(targetContact.person?.cell)
    ) {
      items.push(defaultCountry === "US" ? "cell" : "mobile");
    }

    const targetContactPhone =
      targetContact.person?.phone ||
      targetContact.company?.phone ||
      targetContact.trust?.phone;
    const currentContactPhone =
      currentContact.person?.phone ||
      currentContact.company?.phone ||
      currentContact.trust?.phone;
    if (
      targetContactPhone !== undefined &&
      formatPhoneNumber(targetContactPhone) !==
        formatPhoneNumber(currentContactPhone)
    ) {
      items.push("phone");
    }

    const targetContactEmail =
      targetContact.person?.email || targetContact.company?.email;
    const currentContactEmail =
      currentContact.person?.email || currentContact.company?.email;
    if (
      targetContactEmail !== undefined &&
      targetContactEmail !== currentContactEmail
    ) {
      items.push("email");
    }

    const currentContactAddress =
      currentContact.person?.residentialAddress ||
      currentContact.company?.businessAddress ||
      currentContact.trust?.address;
    const currentContactAddressLine =
      currentContactAddress &&
      formatAddressLine(currentContactAddress, defaultCountry);
    const targetContactAddress =
      targetContact.person?.residentialAddress ||
      targetContact.company?.businessAddress ||
      targetContact.trust?.address;
    const targetContactAddressLine =
      targetContactAddress &&
      formatAddressLine(targetContactAddress, defaultCountry);
    if (
      targetContactAddress !== undefined &&
      currentContactAddressLine !== targetContactAddressLine
    ) {
      items.push("address");
    }

    if (items.length === 0) return "";

    const last = items.pop();
    return items.length > 0 ? `${items.join(", ")} and ${last}` : last;
  };

  const renderDescription = () => {
    if (lookupFailed) {
      return (
        <>
          Could not check for duplicate contacts. Select an option below to
          continue, or close and reopen the review to try again.
        </>
      );
    }
    if (reviewingCurrentContact) {
      return (
        <>
          This form contains a change to the{" "}
          <strong>{buildChangedItems()}</strong> for your matter contact,{" "}
          <strong>{currentContactName}</strong>{" "}
        </>
      );
    }
    return (
      <>
        <strong>
          {matches.length}{" "}
          {matches.length === 1 ? "duplicate contact" : "duplicate contacts"}
        </strong>{" "}
        {matches.length === 1 ? "was" : "were"} found
      </>
    );
  };

  return (
    <div
      className="rounded bg-white overflow-x-hidden"
      data-testid={`role-section-${roleName}`}
    >
      <div className={headerClassNames}>
        <div
          className={clsx("text-paragraph font-semibold", indicatorClassNames)}
          data-testid={`header-${roleName}`}
        >
          {roleName}
        </div>
      </div>
      <div className={headerClassNames}>
        {!loadingMatches && (
          <div className="text-ui" data-testid={`description-${roleName}`}>
            {renderDescription()}
          </div>
        )}
      </div>
      <div className="flex">
        <div className="flex flex-col p-[1.6rem] min-w-[28.2rem] w-[28.2rem] border-r last:border-r-0 border-gray-300">
          {reviewingCurrentContact ? (
            <ContactItem
              contact={currentContact}
              value={currentContact.id}
              isChecked={!!selected && selected === currentContact.id}
              onChange={() => onSelect(currentContact.id)}
              label="Keep Existing Contact"
              description="Keep the current matter contact. The form data will be ignored."
              openContact={
                openContact && currentContactId
                  ? () => openContact?.(currentContactId)
                  : undefined
              }
            />
          ) : (
            <ContactItem
              contact={targetContact}
              value={newContactPlaceholder}
              isChecked={selected === newContactPlaceholder}
              onChange={() => onSelect(newContactPlaceholder)}
              label="Create New Contact"
              description="Add a new contact using the details from the form."
              openContact={undefined}
            />
          )}
        </div>
        <div data-testid="contact-select" className="flex overflow-x-auto">
          {reviewingCurrentContact && (
            <>
              <div
                key={`update-${currentContactId}`}
                className="flex flex-col p-[1.6rem] min-w-[28.2rem] w-[28.2rem]"
              >
                <ContactItem
                  contact={currentContact}
                  value={updateCurrentContactPlaceholder}
                  isChecked={selected === updateCurrentContactPlaceholder}
                  onChange={() => onSelect(updateCurrentContactPlaceholder)}
                  label="Update Existing Contact"
                  description="Update the current matter contact using the details from the form."
                  openContact={
                    openContact && currentContactId
                      ? () => openContact?.(currentContactId)
                      : undefined
                  }
                />
              </div>
              <div
                key={`create-${currentContactId}`}
                className={clsx(
                  "flex flex-col p-[1.6rem] min-w-[28.2rem] w-[28.2rem]",
                )}
              >
                <Tooltip
                  text={
                    isGroupOfPeople
                      ? "Creating a new contact for a group of people is not supported"
                      : undefined
                  }
                  placement="top"
                >
                  <ContactItem
                    contact={targetContact}
                    value={newContactPlaceholder}
                    isChecked={selected === newContactPlaceholder}
                    onChange={() => onSelect(newContactPlaceholder)}
                    label="Create New Contact"
                    description="Add a new contact using the details from the form."
                    openContact={undefined}
                    disabled={isGroupOfPeople}
                  />
                </Tooltip>
              </div>
            </>
          )}
          {loadingMatches ? (
            <div className="flex flex-grow">
              <ProgressCircle size="medium" />
            </div>
          ) : (
            matches.map((match) => (
              <div
                key={match.id}
                className="flex flex-col p-[1.6rem] min-w-[28.2rem] w-[28.2rem]"
              >
                <ContactItem
                  contact={match}
                  value={match.id}
                  isChecked={!!selected && selected === match.id}
                  onChange={() => onSelect(match.id)}
                  label={
                    reviewingCurrentContact
                      ? "Replace Contact"
                      : "Use Existing Contact"
                  }
                  description={
                    reviewingCurrentContact
                      ? "Ignore the form data and replace with an existing contact."
                      : "Ignore the form data and use an existing contact."
                  }
                  openContact={openContact}
                  compareToContact={!hasRoleContact ? targetContact : undefined}
                />
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};
